"use client";

import { c as _c } from "react/compiler-runtime";
import type { ChangeEvent } from "react";
import { useState } from "react";
import { parsePositiveWholeNumberInput } from "../utils/positiveWholeNumberInput";
import VotingSettingBox, { getVotingSettingInputClasses } from "./VotingSettingBox";
type ThresholdTimeUnit = "minutes" | "hours";
const MINUTE_IN_MS = 60 * 1000;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;
const getUnitMs = (unit: ThresholdTimeUnit): number => unit === "hours" ? HOUR_IN_MS : MINUTE_IN_MS;
const getPreferredUnit = (thresholdTimeMs: number | null): ThresholdTimeUnit => {
  if (thresholdTimeMs !== null && Number.isInteger(thresholdTimeMs) && thresholdTimeMs > 0 && thresholdTimeMs % HOUR_IN_MS === 0) {
    return "hours";
  }
  return "minutes";
};
const getDisplayValue = ({
  thresholdTimeMs,
  unit
}: {
  readonly thresholdTimeMs: number | null;
  readonly unit: ThresholdTimeUnit;
}): string => {
  if (thresholdTimeMs === null || !Number.isFinite(thresholdTimeMs) || thresholdTimeMs <= 0) {
    return "";
  }
  return (thresholdTimeMs / getUnitMs(unit)).toString();
};
export default function CreateWaveVotingThresholdTime(t0) {
  const $ = _c(52);
  const {
    thresholdTimeMs,
    errorMessage,
    setThresholdTimeMs
  } = t0;
  let t1;
  if ($[0] !== thresholdTimeMs) {
    t1 = () => getPreferredUnit(thresholdTimeMs);
    $[0] = thresholdTimeMs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [unit, setUnit] = useState(t1);
  const hasError = errorMessage !== undefined;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== errorMessage || $[3] !== hasError || $[4] !== setThresholdTimeMs || $[5] !== thresholdTimeMs || $[6] !== unit) {
    const hasThresholdTime = thresholdTimeMs !== null && Number.isFinite(thresholdTimeMs) && thresholdTimeMs > 0;
    let t15;
    if ($[21] !== thresholdTimeMs || $[22] !== unit) {
      t15 = getDisplayValue({
        thresholdTimeMs,
        unit
      });
      $[21] = thresholdTimeMs;
      $[22] = unit;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    const value = t15;
    let t16;
    if ($[24] !== setThresholdTimeMs || $[25] !== unit) {
      t16 = e => {
        const parsedValue = parsePositiveWholeNumberInput(e.target.value);
        setThresholdTimeMs(parsedValue === null ? null : parsedValue * getUnitMs(unit));
      };
      $[24] = setThresholdTimeMs;
      $[25] = unit;
      $[26] = t16;
    } else {
      t16 = $[26];
    }
    const onThresholdTimeChange = t16;
    let t17;
    if ($[27] !== setThresholdTimeMs || $[28] !== value) {
      t17 = e_0 => {
        const nextUnit = e_0.target.value as ThresholdTimeUnit;
        const parsedValue_0 = parsePositiveWholeNumberInput(value);
        setUnit(nextUnit);
        setThresholdTimeMs(parsedValue_0 === null ? null : parsedValue_0 * getUnitMs(nextUnit));
      };
      $[27] = setThresholdTimeMs;
      $[28] = value;
      $[29] = t17;
    } else {
      t17 = $[29];
    }
    const onUnitChange = t17;
    T0 = VotingSettingBox;
    t8 = "approval-threshold-time-error";
    t9 = errorMessage ?? "";
    t10 = hasError;
    t11 = "approval-threshold-time-help";
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <>Optional. Leave blank to approve as soon as the threshold is reached. Set a time to require the score to stay above the threshold.</>;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    t13 = "approval-threshold-time";
    t14 = "Minimum time above threshold";
    t6 = "tw-grid tw-gap-3 sm:tw-grid-cols-[minmax(0,1fr)_9rem]";
    t7 = <input type="text" inputMode="numeric" autoComplete="off" value={value} onChange={onThresholdTimeChange} id="approval-threshold-time" className={getVotingSettingInputClasses({
      hasError,
      hasValue: hasThresholdTime
    })} aria-invalid={hasError} aria-describedby={hasError ? "approval-threshold-time-error approval-threshold-time-help" : "approval-threshold-time-help"} />;
    t2 = unit;
    t3 = onUnitChange;
    t4 = "Minimum time above threshold unit";
    t5 = getVotingSettingInputClasses({
      hasError,
      hasValue: hasThresholdTime
    });
    $[2] = errorMessage;
    $[3] = hasError;
    $[4] = setThresholdTimeMs;
    $[5] = thresholdTimeMs;
    $[6] = unit;
    $[7] = T0;
    $[8] = t10;
    $[9] = t11;
    $[10] = t12;
    $[11] = t13;
    $[12] = t14;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    T0 = $[7];
    t10 = $[8];
    t11 = $[9];
    t12 = $[10];
    t13 = $[11];
    t14 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t15;
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <option value="minutes">Minutes</option>;
    t16 = <option value="hours">Hours</option>;
    $[31] = t15;
    $[32] = t16;
  } else {
    t15 = $[31];
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t2 || $[34] !== t3 || $[35] !== t4 || $[36] !== t5) {
    t17 = <select value={t2} onChange={t3} aria-label={t4} className={t5}>{t15}{t16}</select>;
    $[33] = t2;
    $[34] = t3;
    $[35] = t4;
    $[36] = t5;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t17 || $[39] !== t6 || $[40] !== t7) {
    t18 = <div className={t6}>{t7}{t17}</div>;
    $[38] = t17;
    $[39] = t6;
    $[40] = t7;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== T0 || $[43] !== t10 || $[44] !== t11 || $[45] !== t12 || $[46] !== t13 || $[47] !== t14 || $[48] !== t18 || $[49] !== t8 || $[50] !== t9) {
    t19 = <T0 errorId={t8} errorMessage={t9} hasError={t10} helpId={t11} helpText={t12} inputId={t13} label={t14}>{t18}</T0>;
    $[42] = T0;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
    $[48] = t18;
    $[49] = t8;
    $[50] = t9;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  return t19;
}